import type { AgentDto, ChannelDto } from '@af/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentOrb } from '@/components/ui/agent-orb';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { qk, useApiMutation } from '@/lib/queries';

/** Create a channel or change its name, topic and members (admins). */
export function ManageChannelDialog({
  channel,
  agents,
  onClose,
}: {
  channel: ChannelDto | null;
  agents: AgentDto[];
  onClose: (created?: ChannelDto) => void;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState(channel?.name ?? '');
  const [topic, setTopic] = useState(channel?.topic ?? '');
  const [members, setMembers] = useState(
    () => new Map((channel?.members ?? []).map((m) => [m.agentId, m.inContext])),
  );
  const invalidate = [qk.channels];
  const create = useApiMutation('createChannel', { invalidate });
  const update = useApiMutation('updateChannel', { invalidate });
  const setM = useApiMutation('setChannelMembers', { invalidate });
  const busy = create.isPending || update.isPending || setM.isPending;
  const save = async () => {
    let c = channel;
    if (!c) c = (await create.mutateAsync({ body: { slug, name, topic } })) as ChannelDto;
    else if (name !== c.name || topic !== c.topic)
      await update.mutateAsync({ params: { id: c.id }, body: { name, topic } });
    await setM.mutateAsync({
      params: { id: c.id },
      body: { members: [...members].map(([agentId, inContext]) => ({ agentId, inContext })) },
    });
    onClose(channel ? undefined : c);
  };
  const reading = [...members.values()].filter(Boolean).length;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        title={channel ? t('channels.manageTitle', { slug: channel.slug }) : t('channels.newTitle')}
        description={t('channels.manageHint')}
        footer={
          <>
            {channel && !channel.archived ? (
              <Button
                variant="ghost"
                className="mr-auto text-danger"
                disabled={busy}
                onClick={() =>
                  void update
                    .mutateAsync({ params: { id: channel.id }, body: { archived: true } })
                    .then(() => onClose())
                }
              >
                {t('channels.archive')}
              </Button>
            ) : null}
            <Button variant="ghost" disabled={busy} onClick={() => onClose()}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!name.trim() || (!channel && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug))}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {!channel ? (
            <Field label={t('channels.slug')} htmlFor="ch-slug">
              <Input
                id="ch-slug"
                value={slug}
                placeholder="launch"
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              />
            </Field>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label={t('channels.name')} htmlFor="ch-name">
              <Input id="ch-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('channels.topic')} htmlFor="ch-topic">
              <Input id="ch-topic" value={topic} maxLength={300} onChange={(e) => setTopic(e.target.value)} />
            </Field>
          </div>
          <fieldset>
            <legend className="mb-1 text-xs text-muted">{t('channels.members')}</legend>
            <ul className="flex max-h-72 flex-col divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {agents.map((a) => {
                const member = members.has(a.id);
                return (
                  <li key={a.id} className="flex items-center gap-2.5 px-3 py-2">
                    <input
                      type="checkbox"
                      id={`ch-m-${a.id}`}
                      checked={member}
                      onChange={(e) =>
                        setMembers((m) => {
                          const n = new Map(m);
                          if (e.target.checked) n.set(a.id, false);
                          else n.delete(a.id);
                          return n;
                        })
                      }
                    />
                    <label htmlFor={`ch-m-${a.id}`} className="flex min-w-0 flex-1 items-center gap-2">
                      <AgentOrb seed={a.id} size={22} avatar={a.avatar} name={a.name} />
                      <span className="truncate text-sm">{a.name}</span>
                      <span className="truncate text-xs text-muted">{a.roleTitle}</span>
                    </label>
                    <span className="text-2xs text-muted">{t('channels.readsAlong')}</span>
                    <Switch
                      aria-label={t('channels.readsAlongFor', { name: a.name })}
                      checked={!!members.get(a.id)}
                      disabled={!member}
                      onCheckedChange={(v) => setMembers((m) => new Map(m).set(a.id, v))}
                    />
                  </li>
                );
              })}
            </ul>
            <p className="mt-1.5 text-xs text-muted">{t('channels.tokenNote', { count: reading })}</p>
          </fieldset>
        </div>
      </DialogContent>
    </Dialog>
  );
}
